import { HttpErrorResponse } from '@angular/common/http';

export function getErrorMessage(
  error: unknown,
  fallback = 'Não foi possível concluir a operação. Tente novamente.'
): string {
  if (error == null) return fallback;

  if (typeof error === 'string') {
    const s = limparMensagem(error);
    return s || fallback;
  }

  if (error instanceof HttpErrorResponse) {
    const fromBody = extrairDeCorpoHttp(error.error);
    if (fromBody) return fromBody;

    if (error.status === 0) {
      return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    }

    const fromParse = extrairTextoDeErroParseJson(error.message);
    if (fromParse) return fromParse;

    if (error.message && !error.message.includes('is not valid JSON') && error.message !== 'Unknown Error') {
      return limparMensagem(error.message);
    }

    if (error.status === 400) {
      return 'Dados inválidos no formulário. Verifique os campos e tente novamente.';
    }
    if (error.status === 401) return 'Não autorizado. Faça login novamente.';
    if (error.status === 403) return 'Você não tem permissão para esta ação.';
    if (error.status === 404) return 'Recurso não encontrado.';
    if (error.status >= 500) return 'Erro interno no servidor. Tente novamente em instantes.';

    return fallback;
  }

  if (error instanceof Error) {
    const fromParse = extrairTextoDeErroParseJson(error.message);
    if (fromParse) return fromParse;

    const nested = (error as Error & { error?: unknown }).error;
    const fromNested = extrairDeCorpoHttp(nested);
    if (fromNested) return fromNested;

    if (error.message) return limparMensagem(error.message);
  }

  if (typeof error === 'object') {
    const fromObj = extrairDeCorpoHttp(error);
    if (fromObj) return fromObj;
  }

  return fallback;
}

function extrairDeCorpoHttp(body: unknown): string | null {
  if (body == null) return null;

  if (typeof body === 'string') {
    const s = limparMensagem(body);
    if (!s) return null;
    if (s.startsWith('{') || s.startsWith('[')) {
      try {
        return extrairDeCorpoHttp(JSON.parse(s));
      } catch {
        return s;
      }
    }
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
      return limparMensagem(s.slice(1, -1));
    }
    return s;
  }

  if (typeof body !== 'object') return null;

  const obj = body as Record<string, unknown>;


  if (typeof obj['text'] === 'string' && obj['text'].trim()) {
    return limparMensagem(obj['text']);
  }

  if (typeof obj['message'] === 'string' && obj['message'].trim()) {
    const msg = limparMensagem(obj['message']);
    const detail = typeof obj['detail'] === 'string' ? limparMensagem(obj['detail']) : '';
    if (detail && detail !== msg && detail.length < 2000 && !detail.includes('at ')) {
      return `${msg}\n\nDetalhes: ${detail}`;
    }

    if (msg.includes('is not valid JSON')) {
      const recovered = extrairTextoDeErroParseJson(msg);
      return recovered || null;
    }
    return msg;
  }

  if (typeof obj['mensagem'] === 'string' && obj['mensagem'].trim()) {
    return limparMensagem(obj['mensagem']);
  }

  if (typeof obj['erro'] === 'string' && obj['erro'].trim()) {
    return limparMensagem(obj['erro']);
  }

  if (typeof obj['title'] === 'string' && obj['title'].trim()) {
    const title = limparMensagem(obj['title']);
    const errors = obj['errors'];
    if (errors && typeof errors === 'object') {
      const parts: string[] = [];
      for (const key of Object.keys(errors as object)) {
        const val = (errors as Record<string, unknown>)[key];
        if (Array.isArray(val)) parts.push(val.map(String).join(', '));
        else if (val != null) parts.push(String(val));
      }
      if (parts.length) return `${title}: ${parts.join(' | ')}`;
    }
    return title;
  }

  if (Array.isArray(obj['erros'])) {
    const partes = (obj['erros'] as unknown[])
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const e = item as Record<string, unknown>;
          return [e['mensagem'], e['message'], e['correcao']].filter(Boolean).join(' — ');
        }
        return null;
      })
      .filter((x): x is string => !!x && x.trim().length > 0);
    if (partes.length) return partes.join('; ');
  }

  return null;
}

function extrairTextoDeErroParseJson(message: string | undefined | null): string | null {
  if (!message) return null;
  if (!message.includes('is not valid JSON') && !message.includes('Unexpected token')) {
    return null;
  }

  const matchDouble = message.match(/"([^"\\]*(?:\\.[^"\\]*)*)"/);
  if (matchDouble?.[1]) {
    const recovered = limparMensagem(matchDouble[1].replace(/\\"/g, '"').replace(/\\n/g, '\n'));
    if (recovered && !recovered.includes('Unexpected token')) {
      if (message.includes('...') && !recovered.endsWith('.')) {
        return `${recovered}… (mensagem incompleta no retorno; verifique certificado, senha e dados do cadastro.)`;
      }
      return recovered;
    }
  }

  const matchSingle = message.match(/'([^']+)'/);
  if (matchSingle?.[1] && matchSingle[1].length > 1 && matchSingle[1] !== 'N') {
    return limparMensagem(matchSingle[1]);
  }

  return 'O servidor retornou um erro em formato inesperado. Verifique certificado digital, senha do certificado e os dados do cadastro.';
}

function limparMensagem(valor: string): string {
  return valor.replace(/\r\n/g, '\n').trim();
}
